import * as React from "react";
import { graphql, type HeadFC, type HeadProps, type PageProps } from "gatsby";
import Layout from "../components/Layout";
import { SEO } from "../components/SEO";
import Section from "../components/Section";
import Post from "../components/Post";
import { clsx } from "clsx";
import ArticleThumbnail from "../components/ArticleThumbnail";
import SeeMoreButton from "../components/SeeMoreButton";

interface IArticlePageContext {
  post: Queries.MicrocmsPost;
}

interface IArticlePageData {
  categories: Queries.MicrocmsPostConnection;
  tags: Queries.MicrocmsPostConnection;
}

const ArticlePage: React.FC<
  PageProps<IArticlePageData, IArticlePageContext>
> = (props: PageProps<IArticlePageData, IArticlePageContext>) => {
  const { data, pageContext } = props;
  const { post } = pageContext;

  return (
    <Layout>
      <Post post={post} />
      {data.categories.totalCount > 0 && (
        <Section headline={`同じ${post.category?.name}カテゴリーの記事`}>
          <div className={clsx("gap-2", "grid", "grid-cols-2")}>
            {data.categories.edges.map((post) => {
              return <ArticleThumbnail key={post.node.id} post={post.node} />;
            })}
          </div>

          {data.categories.pageInfo.hasNextPage && <SeeMoreButton />}
        </Section>
      )}

      <div className={clsx("divider", "m-0")} />

      {data.tags.totalCount > 0 && (
        <Section headline="同じタグの記事">
          <div className={clsx("gap-2", "grid", "grid-cols-2")}>
            {data.tags.edges.map((post) => {
              return <ArticleThumbnail key={post.node.id} post={post.node} />;
            })}
          </div>

          {data.tags.pageInfo.hasNextPage && <SeeMoreButton />}
        </Section>
      )}
    </Layout>
  );
};

export default ArticlePage;

export const Head: HeadFC<Queries.MicrocmsPost, IArticlePageContext> = ({
  pageContext,
}: HeadProps<Queries.MicrocmsPost, IArticlePageContext>) => {
  const { post } = pageContext;

  return (
    <SEO
      description={post.description as string | undefined}
      image={post.eyecatch?.url as string | undefined}
      pathname={`/posts/${post.postId}`}
      title={post.title as string | undefined}
      type="article"
    >
      {/**
       * @see {@link https://ogp.me/#type_article|Object Types article - The Open Graph protocol}
       */}
      {post.category?.name && (
        <meta property="article:section" content={post.category.name} />
      )}
      {post.publishedAt && (
        <meta property="article:published_time" content={post.publishedAt} />
      )}
      {post.tag?.map((tag) => {
        return (
          <meta
            key={tag?.id}
            property="article:tag"
            content={tag?.name ?? ""}
          />
        );
      })}
      {post.updatedAt && (
        <meta property="article:modified_time" content={post.updatedAt} />
      )}
    </SEO>
  );
};

export const query = graphql`
  query ($categoryId: String, $tags: [String], $postId: String) {
    categories: allMicrocmsPost(
      filter: { category: { id: { eq: $categoryId } }, postId: { ne: $postId } }
      limit: 4
    ) {
      edges {
        node {
          category {
            name
            id
          }
          content
          eyecatch {
            url
          }
          id
          title
          postId
          publishedAt
        }
      }

      pageInfo {
        hasNextPage
      }

      totalCount
    }

    tags: allMicrocmsPost(
      filter: {
        tag: { elemMatch: { id: { in: $tags } } }
        postId: { ne: $postId }
      }
      limit: 4
    ) {
      edges {
        node {
          category {
            name
            id
          }
          content
          eyecatch {
            url
          }
          id
          title
          postId
          publishedAt
        }
      }

      pageInfo {
        hasNextPage
      }

      totalCount
    }
  }
`;
